/**
 * 次に落とす料理の抽選（純粋ロジック / Phaser 非依存）。
 *
 * ・最初から高レベルの料理は落下させない（抽選テーブルで制限）
 * ・「今の料理」と「次の料理」の2つを保持する
 * ・乱数は差し替え可能（テスト用）
 */

import type { SpawnWeight } from '../config/types';

export type RandomSource = () => number;

/** 重み付き抽選（純粋関数）。合計が 0 以下の場合は先頭のレベルを返す。 */
export function pickWeightedLevel(weights: readonly SpawnWeight[], rng: RandomSource): number {
  if (weights.length === 0) return 1;
  const total = weights.reduce((sum, w) => sum + Math.max(0, w.weight), 0);
  if (total <= 0) return weights[0].level;

  const target = Math.min(Math.max(rng(), 0), 0.999999) * total;
  let cursor = 0;
  for (const entry of weights) {
    cursor += Math.max(0, entry.weight);
    if (target < cursor) return entry.level;
  }
  return weights[weights.length - 1].level;
}

export interface SpawnSystemOptions {
  weights: readonly SpawnWeight[];
  /** ステージの最大レベル（抽選結果のクランプに使用）。 */
  maxLevel: number;
  rng?: RandomSource;
  /**
   * 指定すると、reset() のたびに乱数を作り直す（「今日の県」で、やり直しても同じ順番にするため）。
   * rng より優先する。
   */
  rngFactory?: () => RandomSource;
}

/** 現在 / 次のレベルを保持するキュー。 */
export class SpawnSystem {
  private readonly weights: SpawnWeight[];
  private readonly maxLevel: number;
  private rng: RandomSource;
  private readonly rngFactory?: () => RandomSource;
  private currentLevel = 1;
  private nextLevel = 1;

  constructor(options: SpawnSystemOptions) {
    this.weights = options.weights
      .filter((w) => w.level >= 1 && w.level <= options.maxLevel && w.weight > 0)
      .map((w) => ({ ...w }));
    if (this.weights.length === 0) {
      this.weights.push({ level: 1, weight: 1 });
    }
    this.maxLevel = options.maxLevel;
    this.rngFactory = options.rngFactory;
    this.rng = options.rngFactory?.() ?? options.rng ?? Math.random;
    this.reset();
  }

  get current(): number {
    return this.currentLevel;
  }

  get next(): number {
    return this.nextLevel;
  }

  /** 抽選対象になる最大レベル（UI 表示・検証用）。 */
  get highestSpawnableLevel(): number {
    return this.weights.reduce((max, w) => Math.max(max, w.level), 1);
  }

  /** current を消費し、next を current へ繰り上げて新しい next を抽選する。 */
  advance(): number {
    const consumed = this.currentLevel;
    this.currentLevel = this.nextLevel;
    this.nextLevel = this.draw();
    return consumed;
  }

  reset(): void {
    if (this.rngFactory) this.rng = this.rngFactory();
    this.currentLevel = this.draw();
    this.nextLevel = this.draw();
  }

  private draw(): number {
    const level = pickWeightedLevel(this.weights, this.rng);
    return Math.min(Math.max(1, level), this.maxLevel);
  }
}
